import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../i18n';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Plus, CheckSquare, Calendar as CalendarIcon, Flag, Trash2, 
  Clock, AlertCircle, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ja, enUS } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TasksPage = () => {
  const { user } = useAuth();
  const t = useTranslation(user?.language || 'ja');
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  
  const [form, setForm] = useState({
    title: '',
    description: '',
    due_date: null,
    priority: 'medium',
  });

  useEffect(() => {
    fetchTasks();
  }, [filter]);

  const fetchTasks = async () => {
    try {
      const params = new URLSearchParams();
      if (filter === 'pending') params.append('done', 'false');
      if (filter === 'completed') params.append('done', 'true');
      
      const { data } = await axios.get(`${API_URL}/api/tasks?${params}`, {
        withCredentials: true,
      });
      setTasks(data.tasks);
    } catch (e) {
      console.error('Failed to fetch tasks:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!form.title.trim()) {
      toast.error(user?.language === 'ja' ? 'タイトルを入力してください' : 'Please enter a title');
      return;
    }
    
    try {
      const payload = {
        ...form,
        due_date: form.due_date ? format(form.due_date, 'yyyy-MM-dd') : null,
      };
      await axios.post(`${API_URL}/api/tasks`, payload, {
        withCredentials: true,
      });
      toast.success(t.common.success);
      setIsCreateOpen(false);
      setForm({ title: '', description: '', due_date: null, priority: 'medium' });
      fetchTasks();
    } catch (e) {
      toast.error(t.common.error);
    }
  };

  const handleToggle = async (taskId, done) => {
    try {
      await axios.put(`${API_URL}/api/tasks/${taskId}?done=${!done}`, {}, {
        withCredentials: true,
      });
      fetchTasks();
    } catch (e) {
      toast.error(t.common.error);
    }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm(t.common.confirm)) return;
    try {
      await axios.delete(`${API_URL}/api/tasks/${taskId}`, {
        withCredentials: true,
      });
      toast.success(t.common.success);
      fetchTasks();
    } catch (e) {
      toast.error(t.common.error);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-destructive';
      case 'medium': return 'text-yellow-400';
      case 'low': return 'text-green-400';
      default: return 'text-muted-foreground';
    }
  };

  const getPriorityBg = (priority) => {
    switch (priority) {
      case 'high': return 'bg-destructive/10 border-destructive/30';
      case 'medium': return 'bg-yellow-500/10 border-yellow-500/30';
      case 'low': return 'bg-green-500/10 border-green-500/30';
      default: return 'bg-secondary';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const locale = user?.language === 'en' ? enUS : ja;
    return format(date, 'PPP', { locale });
  };

  const isOverdue = (dateStr) => {
    if (!dateStr) return false;
    return new Date(dateStr) < new Date();
  };

  const pendingCount = tasks.filter(t => !t.done).length;
  const completedCount = tasks.filter(t => t.done).length;

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="tasks-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-glow">{t.tasks.title}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {user?.language === 'ja' 
              ? `${pendingCount}件の未完了タスク` 
              : `${pendingCount} pending tasks`}
          </p>
        </div>
        
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary w-full sm:w-auto" data-testid="add-task-btn">
              <Plus className="w-4 h-4 mr-2" />
              {t.tasks.addNew}
            </Button>
          </DialogTrigger>
          <DialogContent className="card-cyber border-glow max-w-[95vw] sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{t.tasks.addNew}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>{user?.language === 'ja' ? 'タイトル' : 'Title'} *</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="input-cyber"
                  placeholder={user?.language === 'ja' ? 'タスクのタイトル' : 'Task title'}
                  data-testid="task-title-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>{user?.language === 'ja' ? '説明' : 'Description'}</Label>
                <Input
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="input-cyber"
                  placeholder={user?.language === 'ja' ? '詳細説明（任意）' : 'Description (optional)'}
                  data-testid="task-description-input"
                />
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t.tasks.dueDate}</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start input-cyber"
                        data-testid="task-date-picker"
                      >
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        {form.due_date ? format(form.due_date, 'PPP', { locale: user?.language === 'en' ? enUS : ja }) : (user?.language === 'ja' ? '日付を選択' : 'Select date')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 card-cyber">
                      <Calendar
                        mode="single"
                        selected={form.due_date}
                        onSelect={(date) => setForm({ ...form, due_date: date })}
                        locale={user?.language === 'en' ? enUS : ja}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                
                <div className="space-y-2">
                  <Label>{t.tasks.priority}</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger className="input-cyber" data-testid="task-priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high">
                        <div className="flex items-center gap-2">
                          <Flag className="w-4 h-4 text-destructive" />
                          {t.tasks.high}
                        </div>
                      </SelectItem>
                      <SelectItem value="medium">
                        <div className="flex items-center gap-2">
                          <Flag className="w-4 h-4 text-yellow-400" />
                          {t.tasks.medium}
                        </div>
                      </SelectItem>
                      <SelectItem value="low">
                        <div className="flex items-center gap-2">
                          <Flag className="w-4 h-4 text-green-400" />
                          {t.tasks.low}
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <Button onClick={handleCreate} className="w-full btn-primary" data-testid="task-submit-btn">
                {t.common.save}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 flex-wrap">
        <Button
          variant={filter === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('all')}
          className={filter === 'all' ? 'btn-primary' : 'btn-secondary'}
          data-testid="filter-all"
        >
          {user?.language === 'ja' ? '全て' : 'All'} ({tasks.length})
        </Button>
        <Button
          variant={filter === 'pending' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('pending')}
          className={filter === 'pending' ? 'btn-primary' : 'btn-secondary'}
          data-testid="filter-pending"
        >
          <Clock className="w-4 h-4 mr-1" />
          {t.tasks.pending} ({pendingCount})
        </Button>
        <Button
          variant={filter === 'completed' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('completed')}
          className={filter === 'completed' ? 'btn-primary' : 'btn-secondary'}
          data-testid="filter-completed"
        >
          <CheckCircle className="w-4 h-4 mr-1" />
          {t.tasks.completed} ({completedCount})
        </Button>
      </div>

      {/* Tasks List */}
      <Card className="card-cyber">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground">{t.common.loading}</div>
          ) : tasks.length === 0 ? (
            <div className="p-12 text-center">
              <CheckSquare className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-30" />
              <p className="text-muted-foreground">{t.tasks.noTasks}</p>
            </div>
          ) : (
            <ScrollArea className="h-[calc(100vh-320px)]">
              <div className="divide-y divide-border">
                {tasks.map((task) => (
                  <div
                    key={task.id}
                    className={`flex items-start gap-3 sm:gap-4 p-3 sm:p-4 hover:bg-secondary/50 transition-colors ${
                      task.done ? 'opacity-60' : ''
                    }`}
                    data-testid={`task-item-${task.id}`}
                  >
                    <Checkbox
                      checked={task.done}
                      onCheckedChange={() => handleToggle(task.id, task.done)}
                      className="mt-1"
                      data-testid={`task-checkbox-${task.id}`}
                    />
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`font-medium ${task.done ? 'line-through' : ''}`}>
                          {task.title}
                        </p>
                        <span className={`text-xs px-2 py-0.5 rounded-sm border ${getPriorityBg(task.priority)} ${getPriorityColor(task.priority)}`}>
                          {t.tasks[task.priority]}
                        </span>
                      </div>
                      
                      {task.description && (
                        <p className="text-sm text-muted-foreground mt-1">{task.description}</p>
                      )}
                      
                      {task.due_date && (
                        <div className={`flex items-center gap-1 mt-2 text-xs ${
                          !task.done && isOverdue(task.due_date) ? 'text-destructive' : 'text-muted-foreground'
                        }`}>
                          {!task.done && isOverdue(task.due_date) && (
                            <AlertCircle className="w-3 h-3" />
                          )}
                          <CalendarIcon className="w-3 h-3" />
                          <span className="font-mono">{formatDate(task.due_date)}</span>
                        </div>
                      )}
                    </div>
                    
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-destructive hover:text-destructive"
                      onClick={() => handleDelete(task.id)}
                      data-testid={`delete-task-${task.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TasksPage;
